import React, { useState, useEffect } from 'react';
import { supabase } from '../utils/supabase';
import LearningModule from '../components/LearningModule';
import ProgressChart from '../components/ProgressChart';
import { Search, Filter } from 'lucide-react';

export default function LearningHub() {
  const [modules, setModules] = useState([]);
  const [userProgress, setUserProgress] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('all'); // all, in-progress, completed

  useEffect(() => {
    fetchModules();
    fetchUserProgress();
  }, []);

  const fetchModules = async () => {
    try {
      const { data, error } = await supabase
        .from('learning_modules')
        .select('*')
        .order('created_at', { ascending: true });

      if (error) throw error;
      setModules(data || []);
    } catch (error) {
      console.error('Error fetching modules:', error);
    }
  };

  const fetchUserProgress = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('user_progress')
        .select('*')
        .eq('user_id', user.id);

      if (error) throw error;

      const progressMap = {};
      data?.forEach(progress => {
        progressMap[progress.module_id] = progress;
      });
      setUserProgress(progressMap);
    } catch (error) {
      console.error('Error fetching progress:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleModuleClick = async (moduleId: string) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Update last accessed timestamp
      const { error } = await supabase
        .from('user_progress')
        .upsert({
          user_id: user.id,
          module_id: moduleId,
          last_accessed: new Date().toISOString()
        }, {
          onConflict: 'user_id,module_id'
        });

      if (error) throw error;

      // Navigate to module content (implement this based on your routing setup)
      // navigate(`/module/${moduleId}`);
    } catch (error) {
      console.error('Error updating progress:', error);
    }
  };

  const filteredModules = modules.filter(module => {
    const matchesSearch = module.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         module.description.toLowerCase().includes(searchTerm.toLowerCase());
    
    if (filter === 'all') return matchesSearch;
    const progress = userProgress[module.id];
    if (filter === 'completed') return matchesSearch && progress?.completed;
    if (filter === 'in-progress') return matchesSearch && progress && !progress.completed;
    return matchesSearch;
  });

  const completedCount = Object.values(userProgress).filter((p: any) => p.completed).length;

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <ProgressChart completed={completedCount} total={modules.length} />
        
        <div className="flex gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
            <input
              type="text"
              placeholder="Search modules..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            />
          </div>
          
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          >
            <option value="all">All Modules</option>
            <option value="in-progress">In Progress</option>
            <option value="completed">Completed</option>
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {filteredModules.map((module) => {
          const progress = userProgress[module.id];
          return (
            <LearningModule
              key={module.id}
              title={module.title}
              description={module.description}
              progress={progress?.completed ? 100 : 0}
              duration="2 hours"
              isCompleted={progress?.completed || false}
              onClick={() => handleModuleClick(module.id)}
            />
          );
        })}
      </div>
    </div>
  );
}